import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  InjectionToken,
  input,
  model,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FmntsButtonModule } from '@fmnts/components/button';
import * as Data from 'effect/Data';
import { SegmentedButtonComponent } from './segmented-button.component';

/** Change event object emitted by button toggle. */
export interface ButtonToggleChange {
  /** The button toggle that emits the event. */
  source: ButtonToggleComponent;
  /** The value assigned to the button toggle. */
  value: unknown;
  /** Whether the change was made by the user. */
  isUserInput: boolean;
}

export const ButtonToggleChange = Data.case<ButtonToggleChange>();

/**
 * Injection token used to provide the parent component to button toggles.
 */
export const FMNTS_SEGMENTED_BUTTON_GROUP_COMPONENT =
  new InjectionToken<SegmentedButtonComponent>(
    '@fmnts.components.segmented_button.group_component',
  );

/**
 * Single button inside of a toggle group.
 */
@Component({
  selector: 'fmnts-button-toggle',
  template: `<button
    fmnts-button
    [variant]="variant()"
    [disabled]="disabled()"
    class="inline-flex w-full"
  >
    <ng-content />
  </button>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FmntsButtonModule],
  host: {
    class: 'fmnts-button-toggle inline-flex w-full',
    '[class.fmnts-button-toggle--active]': 'checked()',
  },
})
export class ButtonToggleComponent {
  private readonly cd = inject(ChangeDetectorRef);
  public readonly buttonToggleGroup = inject(
    FMNTS_SEGMENTED_BUTTON_GROUP_COMPONENT,
    {
      optional: true,
    },
  );

  /** Whether the button-toggle is selected. */
  public readonly checked = model(false);

  /** The value */
  public readonly value = input.required<string>();

  /** The variant style of the button. */
  protected readonly variant = computed(
    () => this.buttonToggleGroup?.variant() || 'stroked',
  );

  /** Whether the button is disabled. */
  protected readonly disabled = computed(
    () => this.buttonToggleGroup?.disabled,
  );

  /** Event emitted when the group value changes. */
  @Output() readonly selectionChange = new EventEmitter<ButtonToggleChange>();

  /** Checks the button toggle due to an interaction with the underlying native button. */
  @HostListener('click')
  _onButtonClick(): void {
    if (this.disabled() || this.checked()) {
      return;
    }

    const newValue = !this.checked();
    this.checked.set(newValue);

    // Emit a change event when it's the single selector
    this._emitSelectionChangeEvent(true);
  }

  /** Selects the toggle. */
  public select(): void {
    if (!this.checked()) {
      this.checked.set(true);
      this.cd.markForCheck();
      this._emitSelectionChangeEvent();
    }
  }

  /** Deselects the toggle. */
  public deselect(): void {
    if (this.checked()) {
      this.checked.set(false);
      this.cd.markForCheck();
      this._emitSelectionChangeEvent();
    }
  }

  /** Emits the selection change event. */
  private _emitSelectionChangeEvent(isUserInput = false): void {
    this.selectionChange.emit(
      ButtonToggleChange({
        source: this,
        value: this.value(),
        isUserInput,
      }),
    );
  }
}
